import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import useAudioStore from '../store/audioStore';
import { useLastRead } from '../hooks/useLastRead';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useBookmarks } from '../hooks/useBookmarks';
import { useNotes } from '../hooks/useNotes';
import BookmarksNotesPanel from './BookmarksNotesPanel';
import NoteModal from './NoteModal';
import SearchModal from './SearchModal';
import IndexModal from './IndexModal';
import BottomNavigation from './BottomNavigation';
import SettingsDrawer from './SettingsDrawer';
import { JUZ_DATA, getJuzName, getSurahsInJuz } from '../data/juzData';

const JuzDetail = () => {
  const { number } = useParams();
  const juzNumber = parseInt(number, 10);

  const [ayahs, setAyahs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('fontSize');
    const parsed = saved ? parseInt(saved, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : 32;
  });
  const [lineSpacing, setLineSpacing] = useState(() => {
    return localStorage.getItem('lineSpacing') || 'normal';
  });
  const [fontType, setFontType] = useState(() => {
    return localStorage.getItem('fontType') || 'uthmani';
  });
  const [showEnglish, setShowEnglish] = useState(true);
  const [showUrdu, setShowUrdu] = useState(true);
  const [copiedAyah, setCopiedAyah] = useState(null);
  const [readingMode, setReadingMode] = useState(() => {
    return localStorage.getItem('readingMode') || 'scroll';
  });
  const [currentAyahIndex, setCurrentAyahIndex] = useState(0);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showIndexModal, setShowIndexModal] = useState(false);
  const [showSettingsDrawer, setShowSettingsDrawer] = useState(false);
  const [allSurahs, setAllSurahs] = useState([]);
  const [savedFeedback, setSavedFeedback] = useState(false);
  const [showBookmarksPanel, setShowBookmarksPanel] = useState(false);
  const [activeNoteAyah, setActiveNoteAyah] = useState(null);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);

  const ayahRefs = useRef({});
  const ayahContainerRef = useRef(null);
  const hasAppliedUrlParamsRef = useRef(false);

  const { playAyah, togglePlayPause, currentSurah, currentAyah, status, mode } = useAudioStore();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { saveLastRead } = useLastRead();
  const { toggleBookmark, isBookmarked, hasBookmarks } = useBookmarks();
  const { hasNote } = useNotes();

  const juzInfo = JUZ_DATA.find(j => j.juz === juzNumber) || null;
  const juzName = getJuzName(juzNumber);

  // Persist settings
  useEffect(() => { localStorage.setItem('readingMode', readingMode); }, [readingMode]);
  useEffect(() => { localStorage.setItem('fontSize', String(fontSize)); }, [fontSize]);
  useEffect(() => { localStorage.setItem('lineSpacing', lineSpacing); }, [lineSpacing]);
  useEffect(() => { localStorage.setItem('fontType', fontType); }, [fontType]);

  // Document title
  useEffect(() => {
    document.title = `Juz ${juzNumber} - Quran`;
    return () => { document.title = 'Quran'; };
  }, [juzNumber]);

  const getFontFamily = (ft) => {
    switch (ft) {
      case 'indopak': return "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif";
      case 'tajweed': return "'Scheherazade New', 'Amiri', serif";
      default: return "'Amiri', 'Traditional Arabic', serif";
    }
  };
  const arabicFontFamily = getFontFamily(fontType);

  // Reset on juz change
  useEffect(() => {
    setCurrentAyahIndex(0);
    hasAppliedUrlParamsRef.current = false;
    ayahRefs.current = {};
  }, [juzNumber]);

  // Fetch all surahs for search/index
  useEffect(() => {
    const fetchAllSurahs = async () => {
      try {
        const response = await fetch('/api/surahs');
        if (response.ok) {
          const data = await response.json();
          setAllSurahs(data.data || []);
        }
      } catch (err) {
        console.error('JuzDetail: Error fetching all surahs:', err);
      }
    };
    fetchAllSurahs();
  }, []);

  // Fetch and assemble juz ayahs
  useEffect(() => {
    if (!juzInfo) {
      setError('Invalid Juz number');
      setLoading(false);
      return;
    }

    const fetchJuzAyahs = async () => {
      try {
        setLoading(true);
        setError(null);

        const surahNumbers = getSurahsInJuz(juzNumber);

        // Fetch all surahs in parallel
        const responses = await Promise.all(
          surahNumbers.map(s => fetch(`/api/surahs/${s}`))
        );

        const surahDataList = await Promise.all(
          responses.map(async (r, i) => {
            if (!r.ok) throw new Error(`Failed to fetch surah ${surahNumbers[i]}`);
            const json = await r.json();
            return json.data;
          })
        );

        // Assemble flat ayah list
        const assembled = [];
        surahDataList.forEach((surah) => {
          const sNum = surah.number;
          surah.ayahs.forEach((ayah, idx) => {
            // Determine if this ayah falls within the juz boundaries
            const inStart = sNum > juzInfo.startSurah ||
              (sNum === juzInfo.startSurah && ayah.numberInSurah >= juzInfo.startAyah);
            const inEnd = sNum < juzInfo.endSurah ||
              (sNum === juzInfo.endSurah && ayah.numberInSurah <= juzInfo.endAyah);

            if (!inStart || !inEnd) return;

            assembled.push({
              ...ayah,
              surahNumber: sNum,
              surahName: surah.name,
              surahEnglishName: surah.englishName,
              // Show surah divider on the first included ayah of each surah
              isFirstInSurah: assembled.length === 0 || assembled[assembled.length - 1].surahNumber !== sNum,
              // Show bismillah divider when a surah starts at ayah 1 and it's not Al-Fatihah (already has it) or At-Tawbah (no bismillah)
              showBismillah: ayah.numberInSurah === 1 && sNum !== 1 && sNum !== 9 && idx === 0,
            });
          });
        });

        setAyahs(assembled);
      } catch (err) {
        console.error('JuzDetail: Error fetching juz:', err);
        setError(err.message || 'Failed to load Juz');
      } finally {
        setLoading(false);
      }
    };

    fetchJuzAyahs();
  }, [juzNumber, juzInfo]);

  // Apply URL params after load
  useEffect(() => {
    if (!ayahs.length || hasAppliedUrlParamsRef.current) return;
    const ayahParam = searchParams.get('ayah');
    const modeParam = searchParams.get('mode');
    const ayahIdx = ayahParam ? parseInt(ayahParam, 10) - 1 : null;
    const mode = modeParam === 'ayah' ? 'ayah' : 'scroll';

    if (ayahIdx !== null && ayahIdx >= 0 && ayahIdx < ayahs.length) {
      hasAppliedUrlParamsRef.current = true;
      setReadingMode(mode);
      setCurrentAyahIndex(ayahIdx);
      if (mode === 'scroll') {
        setTimeout(() => {
          const key = `${ayahs[ayahIdx].surahNumber}-${ayahs[ayahIdx].numberInSurah}`;
          const el = ayahRefs.current[key];
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [ayahs, searchParams]);

  // Find topmost visible ayah key for manual save
  const getVisibleAyahIndex = useCallback(() => {
    const viewportTop = window.scrollY + 150;
    let bestIdx = 0;
    let bestDist = Infinity;
    Object.entries(ayahRefs.current).forEach(([key, el]) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const top = rect.top + window.scrollY;
      const dist = Math.abs(top - viewportTop);
      if (dist < bestDist) {
        bestDist = dist;
        // key is "surahNumber-numberInSurah"
        const [sNum, aNum] = key.split('-').map(Number);
        const idx = ayahs.findIndex(a => a.surahNumber === sNum && a.numberInSurah === aNum);
        if (idx !== -1) bestIdx = idx;
      }
    });
    return bestIdx;
  }, [ayahs]);

  const handleSavePlace = useCallback(() => {
    if (!ayahs.length) return;
    const idx = readingMode === 'ayah' ? currentAyahIndex : getVisibleAyahIndex();
    saveLastRead(juzNumber, idx + 1, readingMode, {
      contextMode: 'juz',
      juzNumber,
      translationLang: showEnglish && showUrdu ? 'en-urdu' : showEnglish ? 'en' : 'urdu',
    });
    setSavedFeedback(true);
    setTimeout(() => setSavedFeedback(false), 2000);
  }, [ayahs, juzNumber, readingMode, currentAyahIndex, getVisibleAyahIndex, saveLastRead, showEnglish, showUrdu]);

  const handleToggleAyahBookmark = useCallback((ayah) => {
    toggleBookmark({
      type: 'ayah',
      surahNumber: ayah.surahNumber,
      surahName: ayah.surahName,
      surahEnglishName: ayah.surahEnglishName,
      ayahNumber: ayah.numberInSurah,
      ayahText: ayah.english,
    });
  }, [toggleBookmark]);

  const handleOpenNote = useCallback((ayah) => {
    setActiveNoteAyah(ayah);
    setIsNoteModalOpen(true);
  }, []);

  const handleToggleFocusMode = useCallback(() => {
    setReadingMode(prev => prev === 'ayah' ? 'scroll' : 'ayah');
  }, []);

  useKeyboardShortcuts({ onFocusMode: handleToggleFocusMode });

  // Sync focus mode with playing ayah
  useEffect(() => {
    if (readingMode !== 'ayah' || !ayahs.length || status !== 'playing' || mode !== 'single-ayah') return;
    const idx = ayahs.findIndex(a => a.surahNumber === currentSurah && a.numberInSurah === currentAyah);
    if (idx !== -1) setCurrentAyahIndex(idx);
  }, [currentAyah, currentSurah, readingMode, ayahs, status, mode]);

  // Navigation handlers
  const handlePrevAyah = useCallback(() => {
    if (currentAyahIndex > 0) setCurrentAyahIndex(prev => prev - 1);
  }, [currentAyahIndex]);

  const handleNextAyah = useCallback(() => {
    if (currentAyahIndex < ayahs.length - 1) setCurrentAyahIndex(prev => prev + 1);
  }, [ayahs, currentAyahIndex]);

  // Swipe support for focus mode
  useEffect(() => {
    if (readingMode !== 'ayah' || !ayahContainerRef.current || !ayahs.length) return;
    let touchStartX = 0;
    let touchEndX = 0;
    const handleTouchStart = (e) => { touchStartX = e.changedTouches[0].screenX; };
    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].screenX;
      const diff = touchStartX - touchEndX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) handleNextAyah();
        else handlePrevAyah();
      }
    };
    const container = ayahContainerRef.current;
    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchend', handleTouchEnd);
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [readingMode, ayahs, handleNextAyah, handlePrevAyah]);

  const handlePlayAyah = (ayah) => {
    const isSameAyah = currentSurah === ayah.surahNumber && currentAyah === ayah.numberInSurah;
    const isSingleAyahMode = mode === 'single-ayah';
    if (isSameAyah && isSingleAyahMode && (status === 'playing' || status === 'paused')) {
      togglePlayPause();
      return;
    }
    playAyah(ayah.surahNumber, ayah.numberInSurah, ayah.number);
  };

  const isCurrentAyahPlaying = (ayah) => {
    return currentSurah === ayah.surahNumber && currentAyah === ayah.numberInSurah && status === 'playing';
  };

  const copyAyah = (arabic, english, urdu, absoluteNumber) => {
    let text = arabic;
    if (showUrdu) text += `\n\n${urdu}`;
    if (showEnglish) text += `\n\n${english}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedAyah(absoluteNumber);
      setTimeout(() => setCopiedAyah(null), 2000);
    });
  };

  const getLineHeightClass = () => {
    switch (lineSpacing) {
      case 'compact': return 1.6;
      case 'relaxed': return 2.4;
      default: return 2.0;
    }
  };

  // Loading
  if (loading) {
    return (
      <div style={{ background: 'var(--bg-primary)', minHeight: 'var(--app-height)', paddingTop: 'var(--safe-area-top)', paddingBottom: 'var(--safe-area-bottom)' }}>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Link to="/" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg mb-8 transition-colors" style={{ color: 'var(--text-urdu)', background: 'rgba(148, 163, 184, 0.08)' }}>
            <span>←</span><span>Back</span>
          </Link>
          <div className="text-center py-20">
            <div className="inline-block animate-shimmer rounded-xl p-8 mb-4"><div className="text-5xl">📖</div></div>
            <p className="font-medium" style={{ color: 'var(--text-secondary)' }}>Loading Juz {juzNumber}...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error
  if (error || !juzInfo) {
    return (
      <div style={{ background: 'var(--bg-primary)', minHeight: 'var(--app-height)', paddingTop: 'var(--safe-area-top)', paddingBottom: 'var(--safe-area-bottom)' }}>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Link to="/" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg mb-8 transition-colors" style={{ color: 'var(--text-urdu)', background: 'rgba(148, 163, 184, 0.08)' }}>
            <span>←</span><span>Back</span>
          </Link>
          <div className="text-center py-20">
            <div className="text-5xl mb-4">⚠️</div>
            <p className="text-lg font-semibold mb-4" style={{ color: 'var(--error)' }}>{error || 'Juz not found'}</p>
            <Link to="/" className="inline-block px-6 py-3 rounded-lg font-medium" style={{ background: 'var(--gold-muted)', color: 'var(--gold-hover)', border: '1px solid var(--border-gold)' }}>
              Return to Homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: 'var(--app-height)', paddingTop: 'var(--safe-area-top)', paddingBottom: 'calc(8rem + var(--safe-area-bottom))' }}>
      {/* Sticky Header */}
      <div
        className="sticky z-40 transition-all duration-300 mobile-header"
        style={{ top: 0, background: 'var(--bg-overlay)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(212, 175, 55, 0.1)', paddingTop: 'var(--safe-area-top)' }}
      >
        <div className="max-w-4xl mx-auto px-3 py-2 md:px-4 md:py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-1 px-2 py-1 md:px-3 md:py-2 rounded-lg transition-colors active:opacity-70 mobile-back"
            style={{ color: 'var(--text-urdu)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(148, 163, 184, 0.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <span className="text-lg md:text-xl">←</span>
            <span className="text-xs md:text-sm font-medium">Back</span>
          </Link>

          {/* Juz Selector */}
          <div className="flex-1 text-center px-2 md:px-4 mobile-header-center">
            <select
              value={juzNumber}
              onChange={(e) => navigate(`/juz/${e.target.value}`)}
              className="text-sm font-bold rounded-lg px-3 py-1 appearance-none cursor-pointer mobile-header-title"
              style={{
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border-gold)',
                color: 'var(--text-arabic)',
                fontFamily: 'inherit',
              }}
              aria-label="Select Juz"
            >
              {Array.from({ length: 30 }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>Juz {n}</option>
              ))}
            </select>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <button onClick={() => setShowSearchModal(true)} className="p-2 rounded-lg transition-all active:scale-95" style={{ background: 'rgba(148, 163, 184, 0.08)', border: '1px solid var(--border-default)', color: 'var(--text-secondary)' }} onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gold-muted)'; e.currentTarget.style.borderColor = 'var(--border-gold)'; e.currentTarget.style.color = 'var(--gold-hover)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(148, 163, 184, 0.08)'; e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.color = 'var(--text-secondary)'; }} title="Search">🔍</button>
            <button onClick={() => setShowIndexModal(true)} className="p-2 rounded-lg transition-all active:scale-95" style={{ background: 'rgba(148, 163, 184, 0.08)', border: '1px solid var(--border-default)', color: 'var(--text-secondary)' }} onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gold-muted)'; e.currentTarget.style.borderColor = 'var(--border-gold)'; e.currentTarget.style.color = 'var(--gold-hover)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(148, 163, 184, 0.08)'; e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.color = 'var(--text-secondary)'; }} title="Index">📖</button>
            <button onClick={() => setShowSettingsDrawer(true)} className="p-2 rounded-lg transition-all active:scale-95" style={{ background: 'rgba(148, 163, 184, 0.08)', border: '1px solid var(--border-default)', color: 'var(--text-secondary)' }} onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gold-muted)'; e.currentTarget.style.borderColor = 'var(--border-gold)'; e.currentTarget.style.color = 'var(--gold-hover)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(148, 163, 184, 0.08)'; e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.color = 'var(--text-secondary)'; }} title="Reading Settings">⚙️</button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 md:px-4 py-4 md:py-8">
        {/* Juz Header Card */}
        <div
          className="text-center mb-12 p-12 rounded-2xl surah-header-card"
          style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)' }}
        >
          {/* Arabic opening word */}
          <h1 className="text-arabic text-5xl md:text-6xl font-bold mb-3 surah-header-title-ar"
              style={{ color: 'var(--text-arabic)', fontFamily: arabicFontFamily }}>
            {juzName}
          </h1>
          <h2 className="text-3xl font-semibold mb-2 surah-header-title-en" style={{ color: 'var(--gold-hover)' }}>
            Juz {juzNumber}
          </h2>
          <p className="text-lg mb-6 surah-header-subtitle" style={{ color: 'var(--text-secondary)', opacity: 0.8 }}>
            Surah {juzInfo.startSurah}:{juzInfo.startAyah} → Surah {juzInfo.endSurah}:{juzInfo.endAyah}
          </p>

          <div className="flex gap-3 justify-center mb-8 flex-wrap surah-header-meta">
            <span className="px-4 py-2 rounded-full text-sm font-medium surah-header-pill" style={{ background: 'var(--button-default)', color: 'var(--text-english)', border: '2px solid var(--border-strong)' }}>
              📚 {ayahs.length} Verses
            </span>
            <span className="px-4 py-2 rounded-full text-sm font-medium surah-header-pill" style={{ background: 'var(--button-default)', color: 'var(--text-english)', border: '2px solid var(--border-strong)' }}>
              {juzInfo.endSurah - juzInfo.startSurah + 1} Surahs
            </span>
          </div>

          {/* Prev / Next Juz + Save */}
          <div className="flex gap-3 mt-4 flex-wrap justify-center surah-header-secondary">
            {juzNumber > 1 && (
              <button
                onClick={() => navigate(`/juz/${juzNumber - 1}`)}
                className="px-6 py-3 rounded-xl font-semibold text-sm transition-all active:scale-95"
                style={{ background: 'var(--button-default)', border: '2px solid var(--border-strong)', color: 'var(--text-english)' }}
              >
                ← Juz {juzNumber - 1}
              </button>
            )}
            <button
              onClick={handleSavePlace}
              className="px-6 py-3 rounded-xl font-semibold text-sm transition-all active:scale-95"
              style={{
                background: savedFeedback ? 'var(--success)' : 'var(--button-default)',
                border: '2px solid ' + (savedFeedback ? 'var(--success)' : 'var(--border-strong)'),
                color: savedFeedback ? 'white' : 'var(--text-english)',
              }}
            >
              {savedFeedback ? 'Saved!' : 'Save place'}
            </button>
            {juzNumber < 30 && (
              <button
                onClick={() => navigate(`/juz/${juzNumber + 1}`)}
                className="px-6 py-3 rounded-xl font-semibold text-sm transition-all active:scale-95"
                style={{ background: 'var(--button-default)', border: '2px solid var(--border-strong)', color: 'var(--text-english)' }}
              >
                Juz {juzNumber + 1} →
              </button>
            )}
          </div>
        </div>

        {/* Scroll Mode */}
        {readingMode === 'scroll' && (
          <div className="space-y-6">
            {ayahs.map((ayah, globalIdx) => {
              const isPlaying = isCurrentAyahPlaying(ayah);
              const isAyahBookmarked = isBookmarked(ayah.surahNumber, ayah.numberInSurah);
              const isAyahNoted = hasNote(ayah.surahNumber, ayah.numberInSurah);
              const refKey = `${ayah.surahNumber}-${ayah.numberInSurah}`;

              return (
                <React.Fragment key={`${ayah.surahNumber}-${ayah.number}`}>
                  {/* Surah Divider */}
                  {ayah.isFirstInSurah && (
                    <div
                      className="py-6 text-center rounded-xl"
                      style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-gold)' }}
                    >
                      <p className="text-xs uppercase tracking-widest font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
                        Surah {ayah.surahNumber}
                      </p>
                      <h3
                        className="text-arabic text-3xl font-bold mb-1"
                        style={{ color: 'var(--text-arabic)', fontFamily: arabicFontFamily }}
                      >
                        {ayah.surahName}
                      </h3>
                      <p className="text-lg font-semibold" style={{ color: 'var(--gold-hover)' }}>
                        {ayah.surahEnglishName}
                      </p>
                      {ayah.showBismillah && (
                        <p
                          className="text-arabic text-2xl mt-4"
                          style={{ color: 'var(--text-arabic)', fontFamily: arabicFontFamily, lineHeight: 2 }}
                        >
                          بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ
                        </p>
                      )}
                    </div>
                  )}

                  {/* Ayah Card */}
                  <div
                    ref={(el) => { ayahRefs.current[refKey] = el; }}
                    className={`focus-card ${isPlaying ? 'is-playing' : ''}`}
                  >
                    <div className="focus-card-inner">
                      <div className="flex-shrink-0 pt-1">
                        <div className={`focus-badge ${isPlaying ? 'is-playing' : ''}`}>
                          {ayah.numberInSurah}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-arabic-block focus-arabic ${isPlaying ? 'is-playing' : ''}`}
                          style={{ fontSize: `${fontSize}px`, lineHeight: getLineHeightClass(), fontFamily: arabicFontFamily }}
                        >
                          {ayah.arabic}
                        </div>

                        {showUrdu && (
                          <div className="text-urdu-block focus-translation focus-translation-urdu" style={{ fontSize: `${fontSize - 6}px` }}>
                            {ayah.urdu}
                          </div>
                        )}

                        {showEnglish && (
                          <div className="text-english-block focus-translation focus-translation-en" style={{ fontSize: `${fontSize - 10}px` }}>
                            {ayah.english}
                          </div>
                        )}

                        <div className="focus-actions">
                          <div className="focus-actions-left">
                            <button
                              type="button"
                              onClick={() => copyAyah(ayah.arabic, ayah.english, ayah.urdu, ayah.number)}
                              className={`icon-button ${copiedAyah === ayah.number ? 'is-success' : ''}`}
                              aria-label={copiedAyah === ayah.number ? 'Copied verse' : 'Copy verse'}
                              title={copiedAyah === ayah.number ? 'Copied' : 'Copy verse'}
                            >
                              {copiedAyah === ayah.number ? (
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                              ) : (
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 9h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2Zm-4 6V7a2 2 0 0 1 2-2h8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handlePlayAyah(ayah)}
                              className={`icon-button ${isPlaying ? 'is-playing' : ''}`}
                              aria-label={isPlaying ? 'Pause verse' : 'Play verse'}
                              title={isPlaying ? 'Pause verse' : 'Play verse'}
                            >
                              {isPlaying ? (
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z" fill="currentColor" /></svg>
                              ) : (
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5l11 7-11 7V5z" fill="currentColor" /></svg>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleToggleAyahBookmark(ayah)}
                              className="icon-button"
                              style={{ color: isAyahBookmarked ? 'var(--gold-hover)' : undefined }}
                              aria-label={isAyahBookmarked ? 'Remove bookmark' : 'Bookmark verse'}
                              title={isAyahBookmarked ? 'Remove bookmark' : 'Bookmark verse'}
                            >
                              {isAyahBookmarked ? (
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 3h14a2 2 0 0 1 2 2v16l-9-4-9 4V5a2 2 0 0 1 2-2z" fill="currentColor" /></svg>
                              ) : (
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 3h14a2 2 0 0 1 2 2v16l-9-4-9 4V5a2 2 0 0 1 2-2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleOpenNote(ayah)}
                              className={`icon-button ${isAyahNoted ? 'is-success' : ''}`}
                              style={{ color: isAyahNoted ? 'rgb(129, 140, 248)' : undefined }}
                              aria-label={isAyahNoted ? 'Edit note' : 'Add note'}
                              title={isAyahNoted ? 'Edit note' : 'Add note'}
                            >
                              <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* Focus (Ayah) Mode */}
        {readingMode === 'ayah' && ayahs[currentAyahIndex] && (
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-6 focus-counter">
              <p className="text-base md:text-lg font-bold focus-counter-title" style={{ color: 'var(--text-english)' }}>
                Ayah {currentAyahIndex + 1} of {ayahs.length} · Juz {juzNumber}
              </p>
              <p className="text-xs md:text-sm mt-1 focus-counter-subtitle" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
                Swipe or use buttons below to navigate
              </p>
            </div>

            <div ref={ayahContainerRef} className="mt-4" style={{ touchAction: 'pan-y' }}>
              {(() => {
                const ayah = ayahs[currentAyahIndex];
                const isPlaying = isCurrentAyahPlaying(ayah);
                const isAyahBookmarked = isBookmarked(ayah.surahNumber, ayah.numberInSurah);
                const isAyahNoted = hasNote(ayah.surahNumber, ayah.numberInSurah);

                return (
                  <div className={`focus-card ${isPlaying ? 'is-playing' : ''}`}>
                    {/* Surah context label */}
                    <div className="text-center pb-3 mb-3" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
                        {ayah.surahName} · {ayah.surahEnglishName} · Ayah {ayah.numberInSurah}
                      </span>
                    </div>

                    <div className="focus-card-inner">
                      <div className="flex-shrink-0 pt-1">
                        <div className={`focus-badge ${isPlaying ? 'is-playing' : ''}`}>
                          {ayah.numberInSurah}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-arabic-block focus-arabic ${isPlaying ? 'is-playing' : ''}`}
                          style={{ fontSize: `${fontSize}px`, lineHeight: getLineHeightClass(), fontFamily: arabicFontFamily }}
                        >
                          {ayah.arabic}
                        </div>

                        {showUrdu && (
                          <div className="text-urdu-block focus-translation focus-translation-urdu" style={{ fontSize: `${fontSize - 6}px`, lineHeight: '2.5' }}>
                            {ayah.urdu}
                          </div>
                        )}

                        {showEnglish && (
                          <div className="text-english-block focus-translation focus-translation-en" style={{ fontSize: `${fontSize - 10}px` }}>
                            {ayah.english}
                          </div>
                        )}

                        <div className="focus-actions">
                          <div className="focus-actions-left">
                            <button
                              type="button"
                              onClick={() => copyAyah(ayah.arabic, ayah.english, ayah.urdu, ayah.number)}
                              className={`icon-button ${copiedAyah === ayah.number ? 'is-success' : ''}`}
                              aria-label={copiedAyah === ayah.number ? 'Copied verse' : 'Copy verse'}
                              title={copiedAyah === ayah.number ? 'Copied' : 'Copy verse'}
                            >
                              {copiedAyah === ayah.number ? (
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                              ) : (
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 9h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2Zm-4 6V7a2 2 0 0 1 2-2h8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handlePlayAyah(ayah)}
                              className={`icon-button ${isPlaying ? 'is-playing' : ''}`}
                              aria-label={isPlaying ? 'Pause verse' : 'Play verse'}
                              title={isPlaying ? 'Pause verse' : 'Play verse'}
                            >
                              {isPlaying ? (
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z" fill="currentColor" /></svg>
                              ) : (
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5l11 7-11 7V5z" fill="currentColor" /></svg>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleToggleAyahBookmark(ayah)}
                              className="icon-button"
                              style={{ color: isAyahBookmarked ? 'var(--gold-hover)' : undefined }}
                              aria-label={isAyahBookmarked ? 'Remove bookmark' : 'Bookmark verse'}
                              title={isAyahBookmarked ? 'Remove bookmark' : 'Bookmark verse'}
                            >
                              {isAyahBookmarked ? (
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 3h14a2 2 0 0 1 2 2v16l-9-4-9 4V5a2 2 0 0 1 2-2z" fill="currentColor" /></svg>
                              ) : (
                                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 3h14a2 2 0 0 1 2 2v16l-9-4-9 4V5a2 2 0 0 1 2-2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleOpenNote(ayah)}
                              className={`icon-button ${isAyahNoted ? 'is-success' : ''}`}
                              style={{ color: isAyahNoted ? 'rgb(129, 140, 248)' : undefined }}
                              aria-label={isAyahNoted ? 'Edit note' : 'Add note'}
                              title={isAyahNoted ? 'Edit note' : 'Add note'}
                            >
                              <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="focus-nav">
              <button onClick={handlePrevAyah} disabled={currentAyahIndex === 0} className="focus-nav-btn">
                <span className="focus-nav-icon" aria-hidden="true">←</span>
                <span className="focus-nav-text">Previous Ayah</span>
              </button>
              <button onClick={handleNextAyah} disabled={currentAyahIndex === ayahs.length - 1} className="focus-nav-btn">
                <span className="focus-nav-text">Next Ayah</span>
                <span className="focus-nav-icon" aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals and Panels */}
      <SearchModal isOpen={showSearchModal} onClose={() => setShowSearchModal(false)} surahs={allSurahs} />
      <IndexModal isOpen={showIndexModal} onClose={() => setShowIndexModal(false)} surahs={allSurahs} />
      <SettingsDrawer
        isOpen={showSettingsDrawer}
        onClose={() => setShowSettingsDrawer(false)}
        fontSize={fontSize}
        setFontSize={setFontSize}
        fontType={fontType}
        setFontType={setFontType}
        lineSpacing={lineSpacing}
        setLineSpacing={setLineSpacing}
        showUrdu={showUrdu}
        setShowUrdu={setShowUrdu}
        showEnglish={showEnglish}
        setShowEnglish={setShowEnglish}
        readingMode={readingMode}
        setReadingMode={setReadingMode}
      />
      <NoteModal
        isOpen={isNoteModalOpen}
        onClose={() => setIsNoteModalOpen(false)}
        surahNumber={activeNoteAyah?.surahNumber}
        ayahNumber={activeNoteAyah?.numberInSurah}
        surahName={activeNoteAyah?.surahName}
        ayahText={activeNoteAyah?.english}
      />
      <BookmarksNotesPanel isOpen={showBookmarksPanel} onClose={() => setShowBookmarksPanel(false)} />
      <BottomNavigation
        onSearchClick={() => setShowSearchModal(true)}
        onIndexClick={() => setShowIndexModal(true)}
        onSettingsClick={() => setShowSettingsDrawer(true)}
        hasBookmarks={hasBookmarks}
        onBookmarksClick={() => setShowBookmarksPanel(true)}
      />
    </div>
  );
};

export default JuzDetail;
